/**
 * HomaRecommendations Component
 * Displays Homa (fire ritual) recommendations based on panchanga elements
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Flame, Info, ChevronDown, ChevronRight } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import ErrorDisplay from '../ErrorDisplay/ErrorDisplay';
import type { Locale } from '../../types/api';
import { uiString } from '../../i18n/uiStrings';

interface GodRecommendation {
  name: string;
  benefit: string | null;
  source: string;
}

interface HomaRecommendationResponse {
  recommended_gods: GodRecommendation[];
  recommendations_text: string;
  references: string;
}

interface HomaRecommendationsProps {
  locale?: Locale;
  /** Fetched by DailyPanchangaView with aṅga numbers (B-139-REQ); null while loading. */
  preloadedData?: HomaRecommendationResponse | null;
  /** Set when that fetch failed. */
  error?: string | null;
  onNavigateToArticle?: (articleId: string) => void;
}

/**
 * Presentational: the request is made once, by number, in DailyPanchangaView.
 * The component's own fetch posted the display sentences and was removed in B-139-CON.
 */
export const HomaRecommendations: React.FC<HomaRecommendationsProps> = ({
  locale = 'en',
  preloadedData,
  error = null,
  onNavigateToArticle,
}) => {
  const L = (key: Parameters<typeof uiString>[1]) => uiString(locale, key);
  const data = preloadedData ?? null;
  const loading = !data && !error;
  const [isDeityInfoExpanded, setIsDeityInfoExpanded] = useState<boolean>(false);

  if (loading) {
    return <div role="status" aria-live="polite"><LoadingSpinner message="Loading Homa recommendations..." /></div>;
  }

  if (error) {
    return <ErrorDisplay message={error} onRetry={() => window.location.reload()} />;
  }

  if (!data || data.recommended_gods.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2 text-brand dark:text-brand-dark">
            <Flame className="h-5 w-5" />
            {L('homaTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No Homa recommendations available for this day.</p>
        </CardContent>
      </Card>
    );
  }

  // Deity names + source chips are secondary metadata → muted warm tone
  // (--kg-meta), matching the Panchanga badges. Indigo is reserved for links.
  const metaStyle: React.CSSProperties = { color: 'var(--kg-meta)' };
  const badgeStyle: React.CSSProperties = {
    color: 'var(--kg-meta)',
    borderColor: 'var(--kg-meta-border)',
    background: 'transparent',
  };

  const getSourceLabel = (source: string) => {
    const sources = source.split(', ');
    return sources.map(s => {
      if (s === 'tithi') return L('tithi');
      if (s === 'vaara') return L('vara');
      if (s === 'nakshatra') return L('nakshatra');
      return s;
    }).join(', ');
  };

  // Get list of god names for the main section
  const godNamesList = data.recommended_gods.map(god => god.name).join(', ');

  return (
    <Card className="kg-warm rounded-2xl" style={{ background: 'var(--kg-card)', border: '1px solid var(--kg-line)', boxShadow: 'var(--kg-shadow)' }}>
      <CardHeader className="pt-4 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="font-fraunces text-[18px] font-medium" style={{ color: 'var(--kg-ink)' }}>
            {L('homaTitle')}
          </CardTitle>
          {onNavigateToArticle && (
            <button
              onClick={() => onNavigateToArticle('how-to-homa')}
              className="kg-link text-[12.5px] font-semibold"
            >
              {L('homaWhatLink')}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pb-4">
        <p className="text-sm text-muted-foreground">
          {L('homaGoodDay')}{' '}
          <span className="font-medium" style={metaStyle}>{godNamesList}</span>.
        </p>
        <p className="text-sm text-muted-foreground">
          {L('homaBeginners')}
        </p>

        {/* Deity Information - Collapsible Section */}
        <div className="border-t border-border/60 pt-3">
          <Button
            variant="ghost"
            onClick={() => setIsDeityInfoExpanded(!isDeityInfoExpanded)}
            className="w-full justify-between p-0 h-auto hover:bg-transparent"
          >
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Info className="h-4 w-4" />
              {L('deityInfo')}
            </h3>
            {isDeityInfoExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
          {isDeityInfoExpanded && (
            <div
              className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3"
              // On expand, bring the revealed content into view (minimal scroll).
              // Also engages the sticky sidebar, which only repositions on scroll.
              ref={(el) => {
                if (el && !el.dataset.revealed) {
                  el.dataset.revealed = '1';
                  el.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
                }
              }}
            >
              {data.recommended_gods.map((god, index) => (
                <div
                  key={index}
                  className="p-3 border rounded-lg bg-card hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-semibold text-sm" style={metaStyle}>{god.name}</h4>
                    <Badge
                      variant="outline"
                      className="text-xs rounded"
                      style={badgeStyle}
                    >
                      {getSourceLabel(god.source)}
                    </Badge>
                  </div>
                  {god.benefit && (
                    <p className="text-sm text-muted-foreground mt-2">{god.benefit}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs text-muted-foreground">
            {L('homaFooter')}{' '}
            <a
              href="https://vedicastrologer.org/homa/download.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="kg-link"
            >
              vedicastrologer.org/homa
            </a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default HomaRecommendations;

